import React, { useMemo, useState } from 'react';
import type { ProjectAssignmentItem } from '../../types/assignmentPointsNote';
import { summarizeProjectSubTasks } from '../../utils/assignmentPointsNoteProgress';
import { ChallengeThumb } from '../assignmentChallenge/ChallengeStage';
import { challengeThemeStyle } from '../../types/assignmentChallenge';

type Props = {
  item: ProjectAssignmentItem;
  memberIdSet?: Set<string> | null;
  activeSubTaskId?: string | null;
  rewardProcessing?: boolean;
  onBack: () => void;
  onOpenMonitoring: () => void;
  onGrantReward?: () => void;
  onSelectSubTask: (subTaskId: string, index: number) => void;
  /** 챌린지 과제 전용 도구(편집·공유·제출 보기) */
  challenge?: {
    submittedCountBySubTask: Record<string, number>;
    /** 챌린지 카드 썸네일·공개 여부 (하위 과제 id 기준) */
    cardInfoBySubTask?: Record<
      string,
      { thumbnailUrl: string | null; kicker: string; isVisible: boolean; missionCount?: number }
    >;
    onEdit: () => void;
    onShare: () => void;
    onOpenSubmissions: (subTaskId: string) => void;
  } | null;
};

type ChallengeTools = NonNullable<Props['challenge']>;

export function ChallengeDetailCardGrid({
  item,
  summaries,
  activeSubTaskId,
  challenge,
  onSelectSubTask,
  onAdd,
}: {
  item: ProjectAssignmentItem;
  summaries: ReturnType<typeof summarizeProjectSubTasks>;
  activeSubTaskId?: string | null;
  challenge: ChallengeTools;
  onSelectSubTask: (subTaskId: string, index: number) => void;
  onAdd?: () => void;
}) {
  return (
    <div
      className={`cp-chl-detail-cards${summaries.length === 1 ? ' is-single' : ''}`}
      style={challengeThemeStyle(item.color)}
    >
      {summaries.length === 0 ? <p className="cp-apn-empty-hint--center">챌린지가 없습니다.</p> : null}
      {summaries.map((s) => {
        const complete = s.pendingStudents === 0 && s.totalStudents > 0;
        const active = activeSubTaskId === s.subTaskId;
        const index = item.subTasks.findIndex((t) => t.id === s.subTaskId);
        const info = challenge.cardInfoBySubTask?.[s.subTaskId];
        const submitted = challenge.submittedCountBySubTask[s.subTaskId] ?? 0;
        const done = s.totalStudents - s.pendingStudents;
        const pct = s.totalStudents > 0 ? Math.round((done / s.totalStudents) * 100) : 0;
        return (
          <button
            key={s.subTaskId}
            type="button"
            className={`cp-chl-detail-card${complete ? ' is-complete' : ''}${active ? ' is-active' : ''}${
              info && !info.isVisible ? ' is-hidden' : ''
            }`}
            onClick={() => onSelectSubTask(s.subTaskId, Math.max(0, index))}
            aria-label={`${s.order}번 챌린지 ${s.title || ''} 체크하기`}
          >
            <span className="cp-chl-detail-card-thumb">
              <ChallengeThumb url={info?.thumbnailUrl ?? null} title={s.title} />
              <span className="cp-chl-detail-card-no">{s.order}</span>
              {info && !info.isVisible ? <span className="cp-chl-detail-card-hidden">숨김</span> : null}
              {submitted > 0 ? (
                <span className="cp-chl-detail-card-subs">제출 {submitted}</span>
              ) : null}
            </span>
            <span className="cp-chl-detail-card-body">
              {info?.kicker ? <span className="cp-chl-detail-card-kicker">{info.kicker}</span> : null}
              <span className="cp-chl-detail-card-title">{s.title || '챌린지'}</span>
              <span className="cp-chl-detail-card-meta">
                <span>{s.points}pt</span>
                <span className={`cp-chl-detail-card-status${complete ? ' is-done' : ''}`}>
                  {s.totalStudents > 0
                    ? complete
                      ? '모두 확인'
                      : `미확인 ${s.pendingStudents}/${s.totalStudents}명`
                    : '기록 없음'}
                </span>
              </span>
              <span className="cp-chl-detail-card-progress" aria-hidden>
                <span style={{ width: `${pct}%` }} />
              </span>
            </span>
          </button>
        );
      })}
      {onAdd ? (
        <button type="button" className="cp-chl-detail-card-add" onClick={onAdd}>
          <span aria-hidden>+</span>
          챌린지 추가
        </button>
      ) : null}
    </div>
  );
}

const ProjectAssignmentDetailSection: React.FC<Props> = ({
  item,
  memberIdSet,
  activeSubTaskId,
  rewardProcessing = false,
  onBack,
  onOpenMonitoring,
  onGrantReward,
  onSelectSubTask,
  challenge = null,
}) => {
  const [sortDesc, setSortDesc] = useState(true);
  const summaries = useMemo(() => {
    const list = summarizeProjectSubTasks(item, memberIdSet);
    return sortDesc ? list.slice().reverse() : list;
  }, [item, memberIdSet, sortDesc]);

  return (
    <div className="cp-apn-detail" data-tutorial-apn="project-detail">
      <div className="cp-apn-detail-back-wrap">
        <button type="button" className="cp-apn-detail-back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M7 14L12 9L17 14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          뒤로가기
        </button>
      </div>

      <div className="cp-apn-detail-monitor-head">
        <h3 className="cp-apn-detail-monitor-title">
          {challenge ? '챌린지 도달 모니터링' : '프로젝트 과제 도달 모니터링'}
        </h3>
        <div className="cp-apn-detail-monitor-actions">
          {challenge ? (
            <>
              <button type="button" className="cp-apn-detail-monitor-btn cp-chl-detail-btn" onClick={challenge.onEdit}>
                인증과제 편집
              </button>
              <button type="button" className="cp-apn-detail-monitor-btn cp-chl-detail-btn" onClick={challenge.onShare}>
                참여 링크 · QR
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="cp-apn-detail-monitor-btn"
            data-tutorial-apn="project-reach"
            onClick={onOpenMonitoring}
          >
            멤버별 도달률 확인하기
          </button>
          {item.rewardEnabled ? (
            <button
              type="button"
              className="cp-apn-detail-monitor-btn"
              data-tutorial-apn="project-reward"
              disabled={rewardProcessing}
              onClick={onGrantReward}
            >
              {rewardProcessing ? '보상 처리 중...' : challenge ? '챌린지 보상 지급' : '프로젝트 과제 보상 지급'}
            </button>
          ) : null}
        </div>
      </div>

      <div className="cp-apn-detail-section" data-tutorial-apn="project-subtasks">
        <h4 className="cp-apn-detail-section-title">{challenge ? '챌린지 현황' : '하위과제 현황'}</h4>
        <div className="cp-apn-detail-filter-row">
          <button
            type="button"
            className="cp-apn-detail-filter-btn"
            onClick={() => setSortDesc((v) => !v)}
          >
            <span className="cp-apn-detail-filter-dot" aria-hidden />
            {sortDesc ? '내림차순 순서' : '오름차순 순서'}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {challenge ? (
          <ChallengeDetailCardGrid
            item={item}
            summaries={summaries}
            activeSubTaskId={activeSubTaskId}
            challenge={challenge}
            onSelectSubTask={onSelectSubTask}
          />
        ) : (
        <div className="cp-apn-detail-rows">
          {summaries.length === 0 ? (
            <p className="cp-apn-empty-hint--center">하위과제가 없습니다.</p>
          ) : (
            summaries.map((s) => {
              const complete = s.pendingStudents === 0 && s.totalStudents > 0;
              const active = activeSubTaskId === s.subTaskId;
              const index = item.subTasks.findIndex((t) => t.id === s.subTaskId);
              return (
                <button
                  key={s.subTaskId}
                  type="button"
                  className={`cp-apn-detail-row${complete ? ' is-complete' : ''}${
                    active ? ' is-active' : ''
                  }`}
                  onClick={() => onSelectSubTask(s.subTaskId, Math.max(0, index))}
                >
                  <span className="cp-apn-detail-row-main">
                    <span className="cp-apn-detail-row-title">
                      {s.order}. {s.title || '하위 과제'}
                    </span>
                    <span className="cp-apn-detail-row-sub">포인트: {s.points}</span>
                  </span>
                  <span className={`cp-apn-detail-row-status${complete ? ' is-done' : ''}`}>
                    {s.totalStudents > 0
                      ? complete
                        ? '과제 완료'
                        : `미확인: ${s.pendingStudents}/${s.totalStudents}명`
                      : '기록 없음'}
                  </span>
                </button>
              );
            })
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default ProjectAssignmentDetailSection;
